import React, { useState, useEffect } from 'react';
import {
  Button,
  Container,
  FormControl,
  InputLabel,
  Box,
  Modal,
  MenuItem,
  Select,
  AppBar,
  Toolbar,
  Paper,
  TextField,
  Typography,
  Tooltip,
  Snackbar,
} from '@mui/material'; 
import LoadingBar from 'react-top-loading-bar';
import { useNavigate, useParams } from 'react-router-dom';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const EditTeacherAllTopic = () => {
  const { topicId } = useParams();
  const navigate = useNavigate();

  // State for form fields
  const [teacherId, setTeacherId] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [topicOrder, setTopicOrder] = useState('');
  const [topicName, setTopicName] = useState('');

  // State for dropdown data
  const [teacherData, setTeacherData] = useState([]);
  const [classData, setClassData] = useState([]);
  const [sectionData, setSectionData] = useState([]);
  const [subjectData, setSubjectData] = useState([]);

  // State for UI
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
const [status, setStatus] = useState('P'); // Default to 'Pending'

const drawerWidth = 240;

  // Fetch Topic Details
  const fetchTopicDetails = async () => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/TeachingTopics/GetById/${topicId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
        }
      );

      if (response.ok) {
        const topicData = await response.json();
        setTopicName(topicData.topicName);
        setTopicOrder(topicData.topicOrder);
        setClassId(topicData.classId);
        setSectionId(topicData.sectionId);
        setSubjectId(topicData.subjectId);
        setTeacherId(topicData.coveredByTeacherId || '');
         setStatus(topicData.status || 'P');
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch topic details');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      setLoadingBarProgress(0);
    }
  };

  // Fetch Topic Details on Component Mount
  useEffect(() => {
    fetchTopicDetails();
  }, [topicId]);

  // Handle Update Topic
  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/TeachingTopics/Update`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({
            topicId: Number(topicId),
            topicOrder: Number(topicOrder),
            topicName,
             status,
          }),
        }
      );

      if (response.ok) {
        setLoadingBarProgress(100);
        setShowSuccessModal(true);
      } else {
        setSnackbarMessage('Unable to update topic.');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
    }
  };

  // Handle Cancel
  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <Box
             component="main"
             sx={{
               width: { md: `calc(100% - ${drawerWidth}px)` },
               ml: { md: `${drawerWidth}px` },
               mt: { xs: 0, sm: '64px' },
               p: 3,
               backgroundColor: '#f4f6f9',
               minHeight: '100vh',
             }}
           >
      <LoadingBar progress={loadingBarProgress} onLoaderFinished={() => setLoadingBarProgress(0)} />
      <AppBar   position="static"
        elevation={0}
        sx={{
          backgroundColor: '#063552',
          borderRadius: '8px 8px 0 0',
          mb: 0,
        }}>
        <Toolbar>
          <Typography variant="h4" component="div" style={{ fontWeight: 'bold' }}>
            Edit Topic
          </Typography>
        </Toolbar>
      </AppBar>
      <Paper elevation={3} style={{ padding: 16, width: '100%', margin: 'auto', marginTop: 6 }}>
        <form onSubmit={handleUpdate} style={{ width: '100%', margin: '0 auto' }}>

          {/* Topic Order */}
          <TextField
            fullWidth
            id="topicOrder"
            label="Topic Order"
            variant="outlined"
            margin="normal"
            type="number"
            value={topicOrder}
            onChange={(e) => setTopicOrder(e.target.value)}
          />

          {/* Topic Name */}
          <TextField
            fullWidth
            id="topicName"
            label="Topic Name"
            variant="outlined"
            margin="normal"
            value={topicName}
            onChange={(e) => setTopicName(e.target.value)}
          />

          <FormControl component="fieldset" margin="normal">
  <Typography component="legend">Status</Typography>
  <Box sx={{ display: 'flex', gap: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <input
      disabled
        type="radio"
        id="pending"
        name="status"
        value="P"
        checked={status === 'P'}
        onChange={(e) => setStatus(e.target.value)}
      />
      <label htmlFor="pending">Pending</label>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <input
      disabled
        type="radio"
        id="completed"
        name="status"
        value="C"
         checked={status === 'C'}
        onChange={(e) => setStatus(e.target.value)}
      />
      <label htmlFor="completed">Completed</label>
    </Box>
  </Box>
</FormControl>


          {/* Update and Cancel Buttons */}
          <div style={{ textAlign: 'center', marginTop: '16px', display: 'flex', justifyContent: 'flex-start' }}>
           <Tooltip
  title={
    !topicOrder
      ? 'Enter Topic Order'
      : !topicName
      ? 'Enter Topic Name'
      : ''
  }
  arrow
>
              <Button
                type="submit"
                variant="contained"
                disabled={!topicOrder || !topicName}
                sx={{ mx: 2, backgroundColor: '#063552', color: '#fff', '&:hover': { backgroundColor: '#b36105' } }}
              >
                UPDATE
              </Button>
            </Tooltip>
            <Button type="button" variant="outlined" color="error" onClick={handleCancel}>
              CANCEL
            </Button>
          </div>
        </form>
      </Paper>

      {/* Success Modal */}
      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: '#063552', padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Topic Updated ✅
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              Topic Updated Successfully
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(-1);
                }}
                variant="contained"
                sx={{ backgroundColor: '#063552', color: '#fff', '&:hover': { backgroundColor: '#bc6705' } }}
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditTeacherAllTopic;
